import React, { useEffect, useState } from 'react';
import { MousePointer2, Check } from 'lucide-react';

export interface LightingExtractionIndicatorProps {
  lightingScheduleSnapshot: string | null;
}

type ScanPhase = 'travel' | 'draw' | 'reading' | 'done' | 'poof';

interface ScanBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

const DRAW_DURATION_MS = 760;
const TRAVEL_DURATION_MS = 420;
const READING_DURATION_MS = 1000;
const DONE_DURATION_MS = 220;
const POOF_DURATION_MS = 240;

const randomInRange = (min: number, max: number) => min + Math.random() * (max - min);

const createRandomScanBox = (): ScanBox => {
  const band = Math.random();
  const w = band < 0.33
    ? randomInRange(24, 32)
    : band < 0.66
      ? randomInRange(30, 44)
      : randomInRange(42, 58);
  const h = band < 0.33
    ? randomInRange(16, 22)
    : band < 0.66
      ? randomInRange(20, 30)
      : randomInRange(26, 44);
  const xPadding = 4;
  const yPadding = 6;
  const x = randomInRange(xPadding, 100 - w - xPadding);
  const y = randomInRange(yPadding, 100 - h - yPadding);
  return { x, y, w, h };
};

export const LightingExtractionIndicator: React.FC<LightingExtractionIndicatorProps> = ({
  lightingScheduleSnapshot,
}) => {
  const [scanBox, setScanBox] = useState<ScanBox | null>(null);
  const [phase, setPhase] = useState<ScanPhase>('travel');
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number }>({ x: 8, y: 8 });
  const [cursorTransitionMs, setCursorTransitionMs] = useState(0);

  useEffect(() => {
    if (!lightingScheduleSnapshot) {
      setScanBox(null);
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const schedule = (ms: number, callback: () => void) => {
      timeoutId = setTimeout(() => {
        if (!cancelled) callback();
      }, ms);
    };

    const runCycle = () => {
      const nextBox = createRandomScanBox();
      const nextStart = { x: nextBox.x, y: nextBox.y };
      const nextEnd = { x: nextBox.x + nextBox.w, y: nextBox.y + nextBox.h };

      setScanBox(null);
      setPhase('travel');
      setCursorTransitionMs(TRAVEL_DURATION_MS);
      setCursorPos(nextStart);

      schedule(TRAVEL_DURATION_MS, () => {
        setScanBox(nextBox);
        setPhase('draw');
        setCursorTransitionMs(DRAW_DURATION_MS);
        setCursorPos(nextEnd);

        schedule(DRAW_DURATION_MS, () => {
          setPhase('reading');
          schedule(READING_DURATION_MS, () => {
            setPhase('done');
            schedule(DONE_DURATION_MS, () => {
              setPhase('poof');
              schedule(POOF_DURATION_MS, runCycle);
            });
          });
        });
      });
    };

    runCycle();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [lightingScheduleSnapshot]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100003,
        padding: '20px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '760px',
          background: 'var(--bg-primary)',
          border: '1px solid var(--border-color)',
          borderRadius: '16px',
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.35)',
          padding: '24px',
        }}
      >
        <div
          style={{
            color: 'var(--text-primary)',
            fontSize: '18px',
            fontWeight: 700,
            marginBottom: '14px',
          }}
        >
          Adding to takeoff using AI...
        </div>

        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: lightingScheduleSnapshot ? undefined : '220px',
            borderRadius: '12px',
            overflow: 'hidden',
            background: 'var(--bg-secondary)',
          }}
        >
          {lightingScheduleSnapshot && (
            <img
              src={lightingScheduleSnapshot}
              alt="Lighting schedule being extracted"
              style={{
                width: '100%',
                maxHeight: '460px',
                objectFit: 'contain',
                borderRadius: '12px',
                display: 'block',
              }}
            />
          )}

          {lightingScheduleSnapshot && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(8, 12, 20, 0.34)',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />
          )}

          {lightingScheduleSnapshot && scanBox && (
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2 }}>
              <div
                style={{
                  position: 'absolute',
                  left: `${scanBox.x}%`,
                  top: `${scanBox.y}%`,
                  width: `${scanBox.w}%`,
                  height: `${scanBox.h}%`,
                  borderRadius: '10px',
                  border: '2px solid #ffffff',
                  boxShadow: '0 0 12px rgba(255, 255, 255, 0.35), 0 0 0 9999px rgba(8, 12, 20, 0.56)',
                  transformOrigin: 'top left',
                  transform: phase === 'draw' ? 'scale(0)' : 'scale(1)',
                  animation: phase === 'draw'
                    ? `lightingScanDragHole ${DRAW_DURATION_MS}ms linear forwards`
                    : phase === 'poof'
                      ? 'lightingScanPoof 240ms ease-out forwards'
                      : undefined,
                }}
              >
                {phase !== 'draw' && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      padding: '6px 11px',
                      borderRadius: '999px',
                      border: phase === 'done'
                        ? '1px solid #86efac'
                        : '1px solid rgba(255, 219, 130, 0.68)',
                      background: phase === 'done'
                        ? 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)'
                        : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      color: phase === 'done' ? '#ecfdf5' : '#ffffff',
                      fontSize: '12px',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      animation: phase === 'poof' ? 'lightingScanLabelPoof 220ms ease-out forwards' : 'lightingScanLabelIn 130ms ease-out',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: phase === 'done'
                        ? '0 3px 12px rgba(34, 197, 94, 0.35), 0 0 0 1px rgba(220, 252, 231, 0.2) inset'
                        : '0 3px 12px rgba(245, 158, 11, 0.35)',
                    }}
                  >
                    {phase === 'done' ? (
                      <>
                        <Check size={13} color="#ffffff" strokeWidth={3} />
                        <span>Read!</span>
                      </>
                    ) : (
                      <span style={{ animation: 'lightingReadingPulse 1.1s ease-in-out infinite' }}>
                        Reading area...
                      </span>
                    )}
                  </div>
                )}

                {phase === 'poof' && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: '-1px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255,255,255,0.9)',
                      animation: 'lightingScanRingPoof 240ms ease-out forwards',
                    }}
                  />
                )}
              </div>

            </div>
          )}

          {lightingScheduleSnapshot && (
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 6 }}>
              <div
                style={{
                  position: 'absolute',
                  left: `${cursorPos.x}%`,
                  top: `${cursorPos.y}%`,
                  width: '38px',
                  height: '38px',
                  transform: 'translate(-22%, -14%)',
                  transition: `left ${cursorTransitionMs}ms linear, top ${cursorTransitionMs}ms linear`,
                  filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.65))',
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    display: 'block',
                  }}
                >
                  <MousePointer2
                    size={34}
                    color="#000000"
                    strokeWidth={3.8}
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                    }}
                  />
                  <MousePointer2
                    size={34}
                    color="#f6b73c"
                    strokeWidth={2.9}
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                    }}
                  />
                  <MousePointer2
                    size={34}
                    color="#fde68a"
                    strokeWidth={2.2}
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      clipPath: 'polygon(0 0, 100% 0, 68% 100%, 0 100%)',
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes lightingScanDragHole {
          from { transform: scale(0); }
          to { transform: scale(1); }
        }
        @keyframes lightingScanLabelIn {
          from { opacity: 0; transform: translate(-50%, -42%); }
          to { opacity: 1; transform: translate(-50%, -50%); }
        }
        @keyframes lightingReadingPulse {
          0% { opacity: 0.78; transform: scale(0.98); }
          50% { opacity: 1; transform: scale(1); }
          100% { opacity: 0.78; transform: scale(0.98); }
        }
        @keyframes lightingScanPoof {
          from { opacity: 1; filter: blur(0px); transform: scale(1); }
          to { opacity: 0; filter: blur(2.5px); transform: scale(1.06); }
        }
        @keyframes lightingScanLabelPoof {
          from { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          to { opacity: 0; transform: translate(-50%, -50%) scale(1.22); }
        }
        @keyframes lightingScanRingPoof {
          from { opacity: 0.9; transform: scale(0.82); }
          to { opacity: 0; transform: scale(1.45); }
        }
      `}</style>
    </div>
  );
};
